import { useMutation } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { DatasetDetailDto } from '@/Domain/api/Simulation/dto/Simulation.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';

type Props = {
  datasetId: number;
  criteria: string;
};

export const EvaluationCriteriaEditor = ({ datasetId, criteria }: Props) => {
  const { t } = useTranslation('simulation_page');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(criteria);
  const [lastSaved, setLastSaved] = useState(criteria);

  // Sync draft with server state when the criteria prop changes and the user has no unsaved edits.
  useEffect(() => {
    setDraft((currentDraft) => {
      if (currentDraft === lastSaved) {
        setLastSaved(criteria);
        return criteria;
      }
      return currentDraft;
    });
  }, [criteria, lastSaved]);

  const updateMutation = useMutation<
    DatasetDetailDto,
    unknown,
    { datasetId: number; evaluation_criteria: string }
  >({
    mutationKey: [QueryKey.simulation.updateDataset],
  });

  const isDirty = draft !== lastSaved;

  const handleSave = () => {
    updateMutation.mutate(
      { datasetId, evaluation_criteria: draft },
      {
        onSuccess: () => {
          setLastSaved(draft);
          toast.success(t('toast_criteria_saved'));
        },
        onError: () => toast.error(t('toast_criteria_save_error')),
      },
    );
  };

  return (
    <div className="rounded-md border" data-testid="evaluation-criteria-editor">
      <button
        type="button"
        data-testid="evaluation-criteria-toggle"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium"
      >
        <span className="flex items-center gap-1.5">
          {open ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          {t('evaluation_criteria_label')}
        </span>
        <span className="text-xs font-normal text-muted-foreground">
          {isDirty ? t('evaluation_criteria_unsaved') : t('evaluation_criteria_hint')}
        </span>
      </button>
      {open && (
        <div className="space-y-2 border-t px-3 pb-3 pt-2">
          <textarea
            data-testid="evaluation-criteria-textarea"
            className="h-56 w-full resize-y rounded-md border bg-background px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-ring"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">{t('evaluation_criteria_tip')}</p>
          <div className="flex justify-end">
            <Button
              data-testid="evaluation-criteria-save"
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? (
                <Spinner className="mr-1.5 h-3.5 w-3.5" />
              ) : (
                <Save className="mr-1.5 h-3.5 w-3.5" />
              )}
              {t('save')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
